<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { API_BASE_URL } from '../config'
import {
  getTaskLoopList,
  addTaskLoop,
  editTaskLoop,
  toggleTaskLoop,
  runTaskLoop,
  deleteTaskLoop,
  LOOP_TYPE_MAP,
  LOOP_TYPE_REVERSE,
  type TaskLoop as ApiTaskLoop,
  type LoopTypeKey,
} from '../api/task-loop'

// 任务接口
interface Task {
  id: number
  title: string
  completed: boolean
  level: string
  type: number | string
  execution_at?: string
}

// Tab 类型
type TabType = '全部' | '工作' | '日常'
const activeTab = ref<TabType>('全部')

// 当前日期
const currentDate = ref(new Date())
const selectedDate = ref<string>('')
const taskList = ref<Task[]>([])
const loading = ref(false)
const isCalendarReady = ref(false)

// 存储当月所有有任务的日期
const taskDates = ref<string[]>([])

// 将日期格式化为 YYYY-MM-DD
const formatDate = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// 获取今天的日期字符串
const getTodayString = () => formatDate(new Date())

// 获取当月任务列表
const fetchTaskList = async (year: number, month: number, type?: number) => {
  isCalendarReady.value = false
  
  try {
    const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`
    let url = `${API_BASE_URL}/api/task?date=${monthStr}`
    
    // 如果有 type 参数，添加到 URL
    if (type !== undefined) {
      url += `&type=${type}`
    }
    
    console.log('Fetching tasks with URL:', url)
    const response = await fetch(url)
    
    if (response.ok) {
      const result = await response.json()
      if (result.code === 1 && result.data) {
        // 处理按日期分组的数据结构 { "2026-04-01": [...], "2026-04-05": [...] }
        const groupedData = result.data
        const allTasks: Task[] = []
        const dates: string[] = []
        
        Object.keys(groupedData).forEach(dateStr => {
          const tasksForDate = groupedData[dateStr]
          if (tasksForDate && tasksForDate.length > 0) {
            dates.push(dateStr)
            tasksForDate.forEach((task: any) => {
              console.log('Task data:', task, 'Type:', task.type, typeof task.type)
              const taskType = typeof task.type === 'string' 
                ? (task.type === '工作' ? 1 : 2)
                : task.type
              console.log('Converted type:', taskType)
              allTasks.push({
                ...task,
                date: dateStr,
                type: taskType,
                execution_at: task.execution_at || ''
              })
            })
          }
        })
        
        taskDates.value = dates
        taskList.value = allTasks
      } else {
        taskDates.value = []
        taskList.value = []
      }
    }
    
    // 如果没有选中日期，自动选中今天
    if (!selectedDate.value) {
      selectedDate.value = getTodayString()
    }
    
    isCalendarReady.value = true
  } catch (error) {
    console.error('获取任务列表失败:', error)
    taskDates.value = []
    taskList.value = []
    isCalendarReady.value = true
  }
}

// 切换选中的日期
const selectDate = (date: string) => {
  selectedDate.value = date
}

const year = computed(() => currentDate.value.getFullYear())
const month = computed(() => currentDate.value.getMonth())

// 监听月份变化，自动获取当月数据
watch([year, month], ([newYear, newMonth]) => {
  // 根据当前 Tab 决定 type 参数
  let typeParam: number | undefined
  if (activeTab.value === '工作') {
    typeParam = 1
  } else if (activeTab.value === '日常') {
    typeParam = 2
  }
  fetchTaskList(newYear, newMonth, typeParam)
}, { immediate: true })

const monthNames = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
const weekDays = ['日', '一', '二', '三', '四', '五', '六']

// 日历天数
const calendarDays = computed(() => {
  const firstDay = new Date(year.value, month.value, 1)
  const lastDay = new Date(year.value, month.value + 1, 0)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - firstDay.getDay())
  
  const days = []
  const today = formatDate(new Date())
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    const dateStr = formatDate(date)
    const dayTasks = getTasksByDate(dateStr)
    const hasCompleted = dayTasks.some(t => t.completed)
    const hasPending = dayTasks.some(t => !t.completed)
    
    days.push({
      date: dateStr,
      day: date.getDate(),
      isCurrentMonth: date.getMonth() === month.value,
      isToday: dateStr === today,
      taskCount: dayTasks.length,
      hasCompleted,
      hasPending,
    })
  }
  
  return days
})

// 获取某日期是否有任务
const getTasksByDate = (dateStr: string): Task[] => {
  return taskList.value.filter(task => task.date === dateStr)
}

// 当前 Tab 对应的类型值
const activeTabType = computed(() => {
  if (activeTab.value === '全部') return 0
  return activeTab.value === '工作' ? 1 : 2
})

// 选中日期的任务（数据已经从后端按 type 筛选过了）
const selectedDateTasks = computed(() => {
  if (!selectedDate.value) return []
  return getTasksByDate(selectedDate.value)
})

// 未完成任务
const incompleteTasks = computed(() => {
  return selectedDateTasks.value.filter(task => !task.completed)
})

// 已完成任务
const completedTasks = computed(() => {
  return selectedDateTasks.value.filter(task => task.completed)
})

// 切换 Tab
const switchTab = (tab: TabType) => {
  activeTab.value = tab
  console.log('Switching tab to:', tab)
  
  // 根据 Tab 类型获取对应的 type 参数
  let typeParam: number | undefined
  if (tab === '工作') {
    typeParam = 1
  } else if (tab === '日常') {
    typeParam = 2
  } else {
    typeParam = undefined // 全部
  }
  
  // 重新请求任务列表
  fetchTaskList(year.value, month.value, typeParam)
}

// 已完成任务展开状态
const showCompleted = ref(true)

const prevMonth = () => {
  currentDate.value = new Date(year.value, month.value - 1, 1)
}

const nextMonth = () => {
  currentDate.value = new Date(year.value, month.value + 1, 1)
}

// 展开/收起已完成任务
const toggleCompleted = () => {
  showCompleted.value = !showCompleted.value
}

// 执行时间设置弹窗
const showExecuteModal = ref(false)
const currentTask = ref<Task | null>(null)
const startTime = ref('')
const endTime = ref('')
const writeToDaily = ref(true)
const isCompletingTask = ref(false)

// 获取当前时间（分钟取整到10的倍数）
const getRoundedTime = (date: Date) => {
  const hours = date.getHours()
  const minutes = Math.floor(date.getMinutes() / 10) * 10
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// 打开执行时间设置弹窗
const openExecuteModal = (task: Task) => {
  currentTask.value = task
  const raw = task.execution_at || ''
  if (raw && raw.indexOf('-') !== -1) {
    const parts = raw.split('-').filter(Boolean)
    startTime.value = parts[0] || ''
    endTime.value = parts[1] || parts[0] || ''
  } else if (raw) {
    startTime.value = raw || ''
    endTime.value = raw || ''
  } else {
    // 默认设置：开始时间=当前时间前1小时，结束时间=当前时间，分钟取整
    const now = new Date()
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000)
    endTime.value = getRoundedTime(now)
    startTime.value = getRoundedTime(oneHourAgo)
  }
  writeToDaily.value = true
  showExecuteModal.value = true
}

// 关闭执行时间弹窗
const closeExecuteModal = () => {
  showExecuteModal.value = false
}

// 开始时间变化
const onStartTimeChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  startTime.value = target.value
  if (!endTime.value) {
    endTime.value = target.value
  }
}

// 结束时间变化
const onEndTimeChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  endTime.value = target.value
}

// 切换写入日程
const toggleWriteDaily = () => {
  writeToDaily.value = !writeToDaily.value
}

// 提交执行时间
const submitExecute = async () => {
  if (writeToDaily.value && (!startTime.value || !endTime.value)) {
    alert('请选择开始和结束时间')
    return
  }
  
  if (!currentTask.value?.id) return
  if (isCompletingTask.value) return
  isCompletingTask.value = true
  
  try {
    const schedule = writeToDaily.value ? 1 : 0
    const response = await fetch(`${API_BASE_URL}/api/task/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: currentTask.value.id,
        date: selectedDate.value,
        startTime: startTime.value || '',
        endTime: endTime.value || '',
        schedule
      })
    })
    const body = await response.json()
    if (body.code === 1) {
      currentTask.value.execution_at = startTime.value && endTime.value
        ? `${startTime.value}-${endTime.value}`
        : startTime.value || ''
      currentTask.value.completed = true
      closeExecuteModal()
    }
  } catch (error) {
    console.error('提交失败:', error)
  } finally {
    isCompletingTask.value = false
  }
}

// 撤销完成弹窗
const showUndoModal = ref(false)
const undoTask = ref<Task | null>(null)
const isUndoingTask = ref(false)

// 打开撤销完成弹窗
const openUndoModal = (task: Task) => {
  undoTask.value = task
  showUndoModal.value = true
}

// 关闭撤销弹窗
const closeUndoModal = () => {
  showUndoModal.value = false
  undoTask.value = null
}

// 确认撤销
const confirmUndo = async () => {
  if (!undoTask.value?.id) return
  if (isUndoingTask.value) return
  isUndoingTask.value = true
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/task/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: undoTask.value.id })
    })
    const body = await response.json()
    if (body.code === 1) {
      undoTask.value.completed = false
      closeUndoModal()
    }
  } catch (error) {
    console.error('撤销失败:', error)
  } finally {
    isUndoingTask.value = false
  }
}

// 删除任务
const showDeleteModal = ref(false)
const deleteTaskItem = ref<Task | null>(null)
const isDeletingTask = ref(false)

// 任务右键菜单
const taskContextMenu = ref({
  show: false,
  x: 0,
  y: 0,
  task: null as Task | null
})

// 打开删除任务弹窗
const openDeleteModal = (task: Task) => {
  deleteTaskItem.value = task
  showDeleteModal.value = true
}

// 显示任务右键菜单
const showTaskContextMenu = (event: MouseEvent, task: Task) => {
  event.preventDefault()
  event.stopPropagation()
  taskContextMenu.value = {
    show: true,
    x: event.clientX,
    y: event.clientY,
    task
  }
}

// 隐藏任务右键菜单
const hideTaskContextMenu = () => {
  taskContextMenu.value.show = false
  taskContextMenu.value.task = null
}

// 右键菜单 - 删除任务
const taskContextMenuDelete = () => {
  if (taskContextMenu.value.task) {
    openDeleteModal(taskContextMenu.value.task)
  }
  hideTaskContextMenu()
}

// 关闭删除弹窗
const closeDeleteModal = () => {
  if (isDeletingTask.value) return
  showDeleteModal.value = false
  deleteTaskItem.value = null
}

// 确认删除
const confirmDelete = async () => {
  if (!deleteTaskItem.value?.id) return
  if (isDeletingTask.value) return
  isDeletingTask.value = true
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/task/del`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: deleteTaskItem.value.id })
    })
    const body = await response.json()
    if (body.code === 1) {
      taskList.value = taskList.value.filter(item => item.id !== deleteTaskItem.value?.id)
      showDeleteModal.value = false
      deleteTaskItem.value = null
    }
  } catch (error) {
    console.error('删除失败:', error)
  } finally {
    isDeletingTask.value = false
  }
}

// 添加任务弹窗
const showAddModal = ref(false)
const levelOptions = ['重要且紧急', '重要不紧急', '不重要紧急', '不重要不紧急']
const typeOptions = ['工作', '日常']
const newTask = ref({
  title: '',
  levelIndex: 0,
  typeIndex: 0,
  date: ''
})
const isAddingTask = ref(false)

// 打开添加任务弹窗
const addTask = () => {
  showAddModal.value = true
  let defaultTypeIndex = 0 // 默认工作
  if (activeTab.value === '日常') {
    defaultTypeIndex = 1
  }
  
  newTask.value = {
    title: '',
    levelIndex: 0,
    typeIndex: defaultTypeIndex,
    date: selectedDate.value || getTodayString()
  }
}

// 取消添加任务
const cancelAddTask = () => {
  showAddModal.value = false
}

// 优先级变化
const onLevelChange = (e: Event) => {
  const target = e.target as HTMLSelectElement
  newTask.value.levelIndex = Number(target.value)
}

// 类型变化
const onTypeChange = (e: Event) => {
  const target = e.target as HTMLSelectElement
  newTask.value.typeIndex = Number(target.value)
}

// 执行日期变化
const onExecuteDateChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  newTask.value.date = target.value
}

// 提交添加任务
const submitAddTask = async () => {
  if (!newTask.value.title) {
    alert('请输入任务名称')
    return
  }
  if (!newTask.value.date) {
    alert('请选择执行日期')
    return
  }
  
  if (isAddingTask.value) return
  isAddingTask.value = true
  
  const levelId = (newTask.value.levelIndex || 0) + 1
  const typeValue = newTask.value.typeIndex === 0 ? 1 : 2
  const dateValue = newTask.value.date
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/task/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        date: dateValue,
        level: levelId,
        type: typeValue,
        title: newTask.value.title
      })
    })
    const body = await response.json()
    if (body.code === 1) {
      const newTaskItem: Task = {
        id: body.data,
        title: newTask.value.title,
        completed: false,
        level: levelOptions[newTask.value.levelIndex],
        type: typeValue,
        execution_at: '',
        date: dateValue
      }
      taskList.value.unshift(newTaskItem)
      // 如果添加的是当前选中日期的任务，刷新显示
      if (dateValue === selectedDate.value) {
        // 强制刷新
        taskList.value = [...taskList.value]
      }
      showAddModal.value = false
    }
  } catch (error) {
    console.error('添加失败:', error)
  } finally {
    isAddingTask.value = false
  }
}

onMounted(() => {
  // watch 已设置 immediate: true，会自动触发首次加载
  // 无需在此处重复调用 fetchTaskList
})

// ==================== 定时任务管理 ====================

// 循环类型
type LoopType = LoopTypeKey

// 定时任务接口
interface ScheduleTask {
  id: number
  title: string
  loopType: LoopType
  loopCount: number // 0 代表一直循环
  status: number // 1 运行中  0 停止
  startTime: string // HH:MM
  endTime: string   // HH:MM
  level: number // 0~3 优先级索引，对应 levelOptions
  type: number  // 0~1 类型索引，对应 typeOptions
  executedCount?: number
  remark?: string
}

const loopTypeOptions: { value: LoopType; label: string }[] = [
  { value: 'hour', label: '小时' },
  { value: 'day', label: '天' },
  { value: 'month', label: '月' },
  { value: 'quarter', label: '季度' },
  { value: 'year', label: '年' },
]

const getLoopLabel = (t: LoopType) => loopTypeOptions.find(o => o.value === t)?.label || ''

const describeLoop = (s: ScheduleTask) => {
  const label = getLoopLabel(s.loopType)
  if (s.loopCount === 0) return `每${label}（一直循环）`
  return `每${label} × ${s.loopCount}次`
}

const scheduleTasks = ref<ScheduleTask[]>([])
const loadingSchedule = ref(false)

// 后端 → 前端 视图映射
const mapScheduleFromApi = (r: ApiTaskLoop): ScheduleTask => ({
  id: r.id,
  title: r.title || '',
  loopType: LOOP_TYPE_REVERSE[r.loop_type] || 'day',
  loopCount: Number(r.loop_count) || 0,
  status: Number(r.status) || 0,
  startTime: r.start_time || '00:00',
  endTime: r.end_time || '00:00',
  level: Math.max(0, Math.min(3, Number(r.level) - 1)),
  type: Math.max(0, Math.min(1, Number(r.type) - 1)),
  executedCount: Number(r.executed_count) || 0,
  remark: r.remark || '',
})

// 前端 → 后端 提交转换
const toSaveParams = (s: ScheduleTask) => ({
  title: s.title,
  level: s.level + 1,
  type: s.type + 1,
  loop_type: LOOP_TYPE_MAP[s.loopType] || LOOP_TYPE_MAP.day,
  loop_count: s.loopCount,
  start_time: s.startTime,
  end_time: s.endTime,
  status: s.status,
  remark: s.remark || '',
})

// 加载列表
const fetchScheduleList = async () => {
  loadingSchedule.value = true
  try {
    const res = await getTaskLoopList()
    if (res.code === 1 && Array.isArray(res.data)) {
      scheduleTasks.value = res.data.map(mapScheduleFromApi)
    } else {
      scheduleTasks.value = []
    }
  } catch (e) {
    console.error('加载定时任务列表失败:', e)
    scheduleTasks.value = []
  } finally {
    loadingSchedule.value = false
  }
}

onMounted(() => {
  fetchScheduleList()
})

// 切换开关
const togglingScheduleIds = ref<Set<number>>(new Set())
const isTogglingSchedule = (id: number) => togglingScheduleIds.value.has(id)

const toggleSchedule = async (item: ScheduleTask) => {
  if (togglingScheduleIds.value.has(item.id)) return
  togglingScheduleIds.value = new Set(togglingScheduleIds.value).add(item.id)
  try {
    const res = await toggleTaskLoop(item.id)
    if (res.code === 1 && res.data) {
      item.status = res.data.status
    } else {
      alert(res.message || '状态切换失败')
    }
  } catch (e) {
    console.error('切换状态失败:', e)
    alert('切换状态失败')
  } finally {
    const next = new Set(togglingScheduleIds.value)
    next.delete(item.id)
    togglingScheduleIds.value = next
  }
}

// ===== 右键菜单 =====
const scheduleContextMenu = ref({
  show: false,
  x: 0,
  y: 0,
  item: null as ScheduleTask | null,
})

const showScheduleContextMenu = (e: MouseEvent, item: ScheduleTask) => {
  e.preventDefault()
  e.stopPropagation()
  scheduleContextMenu.value = { show: true, x: e.clientX, y: e.clientY, item }
}

const hideScheduleContextMenu = () => {
  scheduleContextMenu.value.show = false
  scheduleContextMenu.value.item = null
}

// ===== 新建 / 编辑弹窗 =====
const showScheduleFormModal = ref(false)
const isEditingSchedule = ref(false)
const isSubmittingSchedule = ref(false)
const scheduleFormData = ref<ScheduleTask>({
  id: 0,
  title: '',
  loopType: 'day',
  loopCount: 0,
  status: 1,
  startTime: '09:00',
  endTime: '10:00',
  level: 0,
  type: 0,
})

const openAddScheduleModal = () => {
  isEditingSchedule.value = false
  scheduleFormData.value = {
    id: 0,
    title: '',
    loopType: 'day',
    loopCount: 0,
    status: 1,
    startTime: '09:00',
    endTime: '10:00',
    level: 0,
    type: 0,
  }
  showScheduleFormModal.value = true
}

const openEditScheduleModal = (item: ScheduleTask) => {
  isEditingSchedule.value = true
  scheduleFormData.value = { ...item }
  showScheduleFormModal.value = true
}

const closeScheduleFormModal = () => {
  if (isSubmittingSchedule.value) return
  showScheduleFormModal.value = false
}

const submitScheduleForm = async () => {
  if (isSubmittingSchedule.value) return
  if (!scheduleFormData.value.title.trim()) {
    alert('请输入任务标题')
    return
  }
  if (!scheduleFormData.value.startTime || !scheduleFormData.value.endTime) {
    alert('请选择执行时间段')
    return
  }
  isSubmittingSchedule.value = true
  try {
    const payload = toSaveParams(scheduleFormData.value)
    let res
    if (isEditingSchedule.value) {
      res = await editTaskLoop({ ...payload, id: scheduleFormData.value.id })
    } else {
      res = await addTaskLoop(payload)
    }
    if (res.code !== 1) {
      alert(res.message || '保存失败')
      return
    }
    showScheduleFormModal.value = false
    await fetchScheduleList()
  } catch (e) {
    console.error('保存定时任务失败:', e)
    alert('保存失败')
  } finally {
    isSubmittingSchedule.value = false
  }
}

// ===== 删除 =====
const showScheduleDeleteModal = ref(false)
const deletingSchedule = ref<ScheduleTask | null>(null)
const isDeletingSchedule = ref(false)

const openScheduleDeleteModal = (item: ScheduleTask) => {
  deletingSchedule.value = item
  showScheduleDeleteModal.value = true
}

const closeScheduleDeleteModal = () => {
  if (isDeletingSchedule.value) return
  showScheduleDeleteModal.value = false
  deletingSchedule.value = null
}

const confirmDeleteSchedule = async () => {
  if (!deletingSchedule.value || isDeletingSchedule.value) return
  const id = deletingSchedule.value.id
  isDeletingSchedule.value = true
  try {
    const res = await deleteTaskLoop(id)
    if (res.code !== 1) {
      alert(res.message || '删除失败')
      return
    }
    scheduleTasks.value = scheduleTasks.value.filter(s => s.id !== id)
    showScheduleDeleteModal.value = false
    deletingSchedule.value = null
  } catch (e) {
    console.error('删除定时任务失败:', e)
    alert('删除失败')
  } finally {
    isDeletingSchedule.value = false
  }
}

// 立即执行
const runningScheduleIds = ref<Set<number>>(new Set())
const isRunningSchedule = (id: number) => runningScheduleIds.value.has(id)

// 顶部 Toast 提示
const toast = ref({
  show: false,
  message: '',
  type: 'success' as 'success' | 'error',
})
let toastTimer: number | null = null

const showToast = (message: string, type: 'success' | 'error' = 'success') => {
  toast.value = { show: true, message, type }
  if (toastTimer) {
    clearTimeout(toastTimer)
    toastTimer = null
  }
  toastTimer = window.setTimeout(() => {
    toast.value.show = false
    toastTimer = null
  }, 5000)
}

const runScheduleNow = async (item: ScheduleTask) => {
  if (runningScheduleIds.value.has(item.id)) return
  runningScheduleIds.value = new Set(runningScheduleIds.value).add(item.id)
  try {
    const res = await runTaskLoop(item.id)
    if (res.code !== 1) {
      showToast(res.message || '执行失败', 'error')
      return
    }
    if (res.data && typeof res.data.status === 'number') {
      item.status = res.data.status
    }
    if (res.data && typeof res.data.executed_count === 'number') {
      item.executedCount = res.data.executed_count
    }
    // 刷新当月任务列表，让生成的任务出现在日历中
    fetchTaskList(year.value, month.value)
    showToast(`任务「${item.title}」执行成功`, 'success')
  } catch (e) {
    console.error('执行定时任务失败:', e)
    showToast('执行失败', 'error')
  } finally {
    const next = new Set(runningScheduleIds.value)
    next.delete(item.id)
    runningScheduleIds.value = next
  }
}

// 右键菜单动作
const onContextEditSchedule = () => {
  if (scheduleContextMenu.value.item) openEditScheduleModal(scheduleContextMenu.value.item)
  hideScheduleContextMenu()
}
const onContextDeleteSchedule = () => {
  if (scheduleContextMenu.value.item) openScheduleDeleteModal(scheduleContextMenu.value.item)
  hideScheduleContextMenu()
}
const onContextRunSchedule = () => {
  if (scheduleContextMenu.value.item) runScheduleNow(scheduleContextMenu.value.item)
  hideScheduleContextMenu()
}
</script>

<template>
  <div class="tasks-page">
    <!-- 顶部提示信息 -->
    <Transition name="toast">
      <div v-if="toast.show" class="toast-container" :class="toast.type">
        <span class="toast-icon">{{ toast.type === 'success' ? '✅' : '❌' }}</span>
        <span class="toast-message">{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- 左侧日历 -->
    <aside class="calendar-section">
      <div class="calendar-header">
        <button class="nav-btn" @click="prevMonth">‹</button>
        <h3>{{ year }}年 {{ monthNames[month] }}</h3>
        <button class="nav-btn" @click="nextMonth">›</button>
      </div>
      <div class="weekdays">
        <span v-for="day in weekDays" :key="day" class="weekday">{{ day }}</span>
      </div>
      <!-- 日历加载中显示占位 -->
      <div v-if="!isCalendarReady" class="calendar-loading">
        <div class="calendar-grid-placeholder">
          <div v-for="n in 42" :key="n" class="day-cell-placeholder"></div>
        </div>
      </div>
      <div v-else class="calendar-grid">
        <button
          v-for="day in calendarDays"
          :key="day.date"
          class="day-cell"
          :class="{
            'other-month': !day.isCurrentMonth,
            'today': day.isToday,
            'selected': selectedDate === day.date,
            'has-completed': day.hasCompleted,
            'has-pending': day.hasPending,
          }"
          @click="selectDate(day.date)"
        >
          <span class="day-number">{{ day.day }}</span>
          <span v-if="day.taskCount > 0" class="task-count">{{ day.taskCount }}</span>
        </button>
      </div>

      <!-- 日历下方：定时任务管理 -->
      <div class="schedule-section">
        <div class="schedule-header">
          <h4 class="schedule-title">定时任务</h4>
          <button class="schedule-add-btn" @click="openAddScheduleModal" title="新增定时任务">+</button>
        </div>

        <div class="schedule-list">
          <div
            v-for="item in scheduleTasks"
            :key="item.id"
            class="schedule-item"
            :class="{ 'is-stopped': item.status === 0 }"
            @contextmenu="showScheduleContextMenu($event, item)"
          >
            <div class="schedule-info">
              <div class="schedule-item-title">
                {{ item.title }}
                <span v-if="isRunningSchedule(item.id)" class="run-tag">执行中</span>
              </div>
              <div class="schedule-meta">
                <span class="meta-time">{{ item.startTime }} ~ {{ item.endTime }}</span>
                <span class="meta-dot">·</span>
                <span class="meta-loop">{{ describeLoop(item) }}</span>
              </div>
            </div>
            <div class="schedule-switch-wrap">
              <span class="schedule-status-text" :class="{ on: item.status === 1 }">
                {{ item.status === 1 ? '开启' : '已停止' }}
              </span>
              <button
                class="schedule-switch"
                :class="{ on: item.status === 1, loading: isTogglingSchedule(item.id) }"
                :disabled="isTogglingSchedule(item.id)"
                @click.stop="toggleSchedule(item)"
              >
                <span class="switch-thumb"></span>
              </button>
            </div>
          </div>

          <div v-if="scheduleTasks.length === 0" class="schedule-empty">
            <p>暂无定时任务</p>
            <button class="schedule-empty-btn" @click="openAddScheduleModal">+ 新建定时任务</button>
          </div>
        </div>
      </div>
    </aside>

    <!-- 右侧任务列表 -->
    <main class="detail-section">
      <div v-if="loading" class="empty-detail">
        <p>加载中...</p>
      </div>
      <div v-else-if="selectedDate" class="tasks-detail">
        <div class="detail-header">
                <h2>{{ selectedDate }}</h2>
          <div class="header-left">
       
            <div class="task-tabs">
              <div 
                class="tab-item" 
                :class="{ active: activeTab === '全部' }"
                @click="switchTab('全部')"
              >
                全部
              </div>
              <div 
                class="tab-item" 
                :class="{ active: activeTab === '工作' }"
                @click="switchTab('工作')"
              >
                工作
              </div>
              <div 
                class="tab-item" 
                :class="{ active: activeTab === '日常' }"
                @click="switchTab('日常')"
              >
                日常
              </div>
            </div>
          </div>
          
          <button class="add-btn-circle" @click="addTask">+</button>
        </div>

        <!-- 任务列表 -->
        <div class="task-list-content">
          <!-- 未完成任务 -->
          <div class="section" v-if="incompleteTasks.length > 0">
            <div 
              class="task-card"
              v-for="task in incompleteTasks" 
              :key="task.id"
              @click="openExecuteModal(task)"
              @contextmenu="showTaskContextMenu($event, task)"
            >
              <div class="task-content">
                <span class="task-title">{{ task.title }}</span>
                <span class="task-subtitle" v-if="task.execution_at">
                  ※{{ task.execution_at }}
                </span>
              </div>
              <div class="task-checkbox" @click.stop="openExecuteModal(task)">
                <div class="checkbox-circle">
                  <span class="checkmark" v-if="task.completed">✓</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 已完成任务 -->
          <div class="completed-section" v-if="completedTasks.length > 0">
            <div class="completed-header" @click="toggleCompleted">
              <span class="chevron" :class="{ expanded: showCompleted }">▶</span>
              <span class="completed-title">已完成 {{ completedTasks.length }}</span>
            </div>
            <div class="completed-list" v-if="showCompleted">
              <div 
                class="task-card completed"
                v-for="task in completedTasks" 
                :key="task.id"
                @click="openUndoModal(task)"
                @contextmenu="showTaskContextMenu($event, task)"
              >
                <div class="task-content">
                  <span class="task-title">{{ task.title }}</span>
                </div>
                <div class="task-checkbox" @click.stop="openUndoModal(task)">
                  <div class="checkbox-circle checked">
                    <span class="checkmark">✓</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div v-if="selectedDateTasks.length === 0" class="empty-tasks">
            <div class="empty-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
                <line x1="8" y1="8" x2="16" y2="8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="8" y1="16" x2="12" y2="16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </div>
            <p>暂无任务</p>
          </div>
        </div>
      </div>
      <div v-else class="empty-detail">
        <p>请选择日期查看任务</p>
      </div>
    </main>

    <!-- 任务执行时间设置弹窗 -->
    <div class="modal-overlay" v-if="showExecuteModal" @click.self="closeExecuteModal">
      <div class="modal-content execute-modal-content">
        <div class="modal-header">
          <h3>{{ currentTask?.title || '设置执行时间' }}</h3>
          <button class="close-btn" @click="closeExecuteModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-row time-row">
            <label class="label">开始时间</label>
            <input 
              type="time" 
              v-model="startTime"
              class="time-input"
            />
          </div>
          <div class="form-row time-row">
            <label class="label">结束时间</label>
            <input 
              type="time" 
              v-model="endTime"
              class="time-input"
            />
          </div>
          <div class="form-row checkbox-row" @click="toggleWriteDaily">
            <label class="label">写入日程表</label>
            <div class="checkbox-circle" :class="{ checked: writeToDaily }">
              <span class="checkmark" v-if="writeToDaily">✓</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" @click="closeExecuteModal" :disabled="isCompletingTask">取消</button>
          <button class="btn primary" @click="submitExecute" :disabled="isCompletingTask">
            <span v-if="isCompletingTask" class="btn-loading"></span>
            <span v-else>确认</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 撤销完成提示弹窗 -->
    <div class="modal-overlay" v-if="showUndoModal" @click.self="closeUndoModal">
      <div class="modal-content execute-modal-content">
        <div class="modal-header">
          <h3>撤销操作</h3>
          <button class="close-btn" @click="closeUndoModal">×</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">确定要撤销"{{ undoTask?.title }}"的完成状态吗？</p>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" @click="closeUndoModal" :disabled="isUndoingTask">取消</button>
          <button class="btn primary" @click="confirmUndo" :disabled="isUndoingTask">
            <span v-if="isUndoingTask" class="btn-loading"></span>
            <span v-else>确定</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 删除任务提示弹窗 -->
    <div class="modal-overlay" v-if="showDeleteModal" @click.self="closeDeleteModal">
      <div class="modal-content execute-modal-content">
        <div class="modal-header">
          <h3>删除任务</h3>
          <button class="close-btn" @click="closeDeleteModal">×</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">确定要删除"{{ deleteTaskItem?.title }}"吗？</p>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" :disabled="isDeletingTask" @click="closeDeleteModal">取消</button>
          <button class="btn danger" :disabled="isDeletingTask" @click="confirmDelete">
            <span v-if="isDeletingTask" class="btn-loading"></span>
            {{ isDeletingTask ? '删除中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 添加任务弹窗 -->
    <div class="modal-overlay" v-if="showAddModal" @click.self="cancelAddTask">
      <div class="modal-content add-modal-content">
        <div class="modal-header">
          <h3>添加任务</h3>
          <button class="close-btn" @click="cancelAddTask">×</button>
        </div>
        <div class="modal-body">
          <div class="title-input-wrap">
            <textarea 
              class="title-textarea" 
              v-model="newTask.title" 
              placeholder="请输入任务名称"
              rows="3"
            ></textarea>
          </div>
          <div class="form-row triple-row">
            <div class="field">
              <label>优先级</label>
              <select :value="newTask.levelIndex" @change="onLevelChange" class="form-select">
                <option v-for="(level, index) in levelOptions" :key="index" :value="index">
                  {{ level }}
                </option>
              </select>
            </div>
            <div class="field">
              <label>类型</label>
              <select :value="newTask.typeIndex" @change="onTypeChange" class="form-select">
                <option v-for="(type, index) in typeOptions" :key="index" :value="index">
                  {{ type }}
                </option>
              </select>
            </div>
            <div class="field">
              <label>日期</label>
              <input 
                type="date" 
                :value="newTask.date"
                @change="onExecuteDateChange"
                class="form-input"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" @click="cancelAddTask" :disabled="isAddingTask">取消</button>
          <button class="btn primary" @click="submitAddTask" :disabled="isAddingTask">
            <span v-if="isAddingTask" class="btn-loading"></span>
            <span v-else>确认</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 任务右键菜单 -->
    <div 
      v-if="taskContextMenu.show" 
      class="context-menu-overlay" 
      @click="hideTaskContextMenu"
      @contextmenu.prevent="hideTaskContextMenu"
    >
      <div 
        class="context-menu" 
        :style="{ left: taskContextMenu.x + 'px', top: taskContextMenu.y + 'px' }"
      >
        <div class="context-menu-item" @click="taskContextMenuDelete">
          <span class="context-menu-icon">🗑️</span>
          <span>删除</span>
        </div>
      </div>
    </div>

    <!-- 定时任务右键菜单 -->
    <div
      v-if="scheduleContextMenu.show"
      class="context-menu-overlay"
      @click="hideScheduleContextMenu"
      @contextmenu.prevent="hideScheduleContextMenu"
    >
      <div
        class="context-menu"
        :style="{ left: scheduleContextMenu.x + 'px', top: scheduleContextMenu.y + 'px' }"
      >
        <div class="context-menu-item" @click="onContextEditSchedule">
          <span>编辑</span>
        </div>
        <div class="context-menu-item" @click="onContextDeleteSchedule">
          <span>删除</span>
        </div>
        <div class="context-menu-item" @click="onContextRunSchedule">
          <span>立即执行</span>
        </div>
      </div>
    </div>

    <!-- 定时任务 新增/编辑 弹窗 -->
    <div v-if="showScheduleFormModal" class="modal-overlay" @click.self="closeScheduleFormModal">
      <div class="modal-content add-modal-content">
        <div class="modal-header">
          <h3>{{ isEditingSchedule ? '编辑定时任务' : '新建定时任务' }}</h3>
          <button class="close-btn" :disabled="isSubmittingSchedule" @click="closeScheduleFormModal">×</button>
        </div>
        <div class="modal-body">
          <div class="sch-row">
            <label class="sch-label">标题</label>
            <input
              v-model="scheduleFormData.title"
              type="text"
              class="sch-input"
              placeholder="任务名称"
              :disabled="isSubmittingSchedule"
            />
          </div>

          <div class="sch-row sch-row-inline">
            <div class="sch-col">
              <label class="sch-label">优先级</label>
              <select v-model.number="scheduleFormData.level" class="sch-input" :disabled="isSubmittingSchedule">
                <option v-for="(level, index) in levelOptions" :key="index" :value="index">
                  {{ level }}
                </option>
              </select>
            </div>
            <div class="sch-col">
              <label class="sch-label">类型</label>
              <select v-model.number="scheduleFormData.type" class="sch-input" :disabled="isSubmittingSchedule">
                <option v-for="(t, index) in typeOptions" :key="index" :value="index">
                  {{ t }}
                </option>
              </select>
            </div>
          </div>

          <div class="sch-row">
            <label class="sch-label">循环类型</label>
            <div class="chip-group">
              <button
                v-for="opt in loopTypeOptions"
                :key="opt.value"
                type="button"
                class="chip"
                :class="{ active: scheduleFormData.loopType === opt.value }"
                :disabled="isSubmittingSchedule"
                @click="scheduleFormData.loopType = opt.value"
              >{{ opt.label }}</button>
            </div>
          </div>

          <div class="sch-row">
            <label class="sch-label">循环次数</label>
            <div class="loop-count-row">
              <input
                v-model.number="scheduleFormData.loopCount"
                type="number"
                min="0"
                class="sch-input sch-input-num"
                :disabled="isSubmittingSchedule || scheduleFormData.loopCount === 0"
                placeholder="次数"
              />
              <label class="infinite-check">
                <input
                  type="checkbox"
                  :checked="scheduleFormData.loopCount === 0"
                  :disabled="isSubmittingSchedule"
                  @change="(e: any) => (scheduleFormData.loopCount = e.target.checked ? 0 : 1)"
                />
                一直循环
              </label>
            </div>
          </div>

          <div class="sch-row">
            <label class="sch-label">执行时间段</label>
            <div class="time-range-row">
              <input
                v-model="scheduleFormData.startTime"
                type="time"
                class="sch-input sch-input-time"
                :disabled="isSubmittingSchedule"
              />
              <span class="time-sep">~</span>
              <input
                v-model="scheduleFormData.endTime"
                type="time"
                class="sch-input sch-input-time"
                :disabled="isSubmittingSchedule"
              />
            </div>
          </div>

          <div class="sch-row">
            <label class="sch-label">初始状态</label>
            <div class="chip-group">
              <button
                type="button"
                class="chip"
                :class="{ active: scheduleFormData.status === 1 }"
                :disabled="isSubmittingSchedule"
                @click="scheduleFormData.status = 1"
              >开启</button>
              <button
                type="button"
                class="chip"
                :class="{ active: scheduleFormData.status === 0 }"
                :disabled="isSubmittingSchedule"
                @click="scheduleFormData.status = 0"
              >停止</button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" :disabled="isSubmittingSchedule" @click="closeScheduleFormModal">取消</button>
          <button class="btn primary" :disabled="isSubmittingSchedule" @click="submitScheduleForm">
            <span v-if="isSubmittingSchedule" class="btn-loading"></span>
            {{ isSubmittingSchedule ? (isEditingSchedule ? '保存中...' : '创建中...') : (isEditingSchedule ? '保存' : '创建') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 定时任务 删除确认弹窗 -->
    <div v-if="showScheduleDeleteModal" class="modal-overlay" @click.self="closeScheduleDeleteModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>删除定时任务</h3>
          <button class="close-btn" :disabled="isDeletingSchedule" @click="closeScheduleDeleteModal">×</button>
        </div>
        <div class="modal-body">
          <p style="margin: 0; color: #666; line-height: 1.6;">确定要删除「{{ deletingSchedule?.title }}」吗？此操作不可恢复。</p>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" :disabled="isDeletingSchedule" @click="closeScheduleDeleteModal">取消</button>
          <button class="btn danger" :disabled="isDeletingSchedule" @click="confirmDeleteSchedule">
            <span v-if="isDeletingSchedule" class="btn-loading"></span>
            {{ isDeletingSchedule ? '删除中...' : '确定删除' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tasks-page {
  display: flex;
  height: 100%;
  background: #f5f5f5;
  position: relative;
}

/* Toast 提示样式 */
.toast-container {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  justify-content: center;
  gap: 10px;
  pointer-events: none;
}

.toast-container.success {
  background-color: #e8f5e9;
  border: 1px solid #42b883;
  color: #2e7d32;
}

.toast-container.error {
  background-color: #ffebee;
  border: 1px solid #f44336;
  color: #c62828;
}

.toast-icon {
  font-size: 1.2rem;
}

.toast-message {
  font-size: 0.95rem;
  font-weight: 500;
}

/* Toast 过渡动画 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translate(-50%, -20px);
}

.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -20px);
}

/* 左侧日历 */
.calendar-section {
  width: 400px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  padding: 1.5rem;
  flex-shrink: 0;
  overflow-y: auto;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.calendar-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.nav-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.nav-btn:hover {
  background: #e0e0e0;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.weekday {
  text-align: center;
  font-size: 0.85rem;
  color: #999;
  padding: 0.5rem;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
}

/* 日历加载占位样式 */
.calendar-grid-placeholder {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
}

.day-cell-placeholder {
  aspect-ratio: 1;
  border-radius: 8px;
  background: #f5f5f5;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.day-cell {
  aspect-ratio: 1;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  transition: all 0.2s;
  position: relative;
  padding: 0.25rem;
}

.day-cell:hover {
  background: #f0f0f0;
}

.day-cell.other-month {
  color: #ccc;
}

.day-cell.today {
  background: #e8f5e9;
  color: #42b883;
  font-weight: 600;
}

.day-cell.selected {
  background: #42b883;
  color: #fff;
}

.day-cell.has-completed::after {
  content: '';
  position: absolute;
  top: 4px;
  left: 4px;
  width: 5px;
  height: 5px;
  background: #42b883;
  border-radius: 50%;
}

.day-cell.has-pending::before {
  content: '';
  position: absolute;
  top: 4px;
  left: 4px;
  width: 5px;
  height: 5px;
  background: #f44336;
  border-radius: 50%;
}

.day-cell.selected.has-completed::after,
.day-cell.selected.has-pending::before {
  background: #fff;
}

.day-number {
  font-size: 0.9rem;
}

.task-count {
  font-size: 0.7rem;
  background: #42b883;
  color: #fff;
  padding: 0 4px;
  border-radius: 8px;
  min-width: 16px;
  text-align: center;
}

.day-cell.selected .task-count {
  background: #fff;
  color: #42b883;
}

/* 右侧详情 */
.detail-section {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.tasks-detail {
  background: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: calc(100% - 3rem);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.detail-header h2 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.task-stats {
  display: flex;
  gap: 0.5rem;
}

.stat {
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
}

.stat.pending {
  background: #fff3e0;
  color: #ff9800;
}

.stat.completed {
  background: #e8f5e9;
  color: #42b883;
}

.add-btn-circle {
  width: 40px;
  height: 40px;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.5rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;
}

.add-btn-circle:hover {
  background: #369970;
}

/* Tab 切换 */
.task-tabs {
  display: flex;
  gap: 0;
  background: #f0f0f0;
  border-radius: 8px;
  padding: 3px;
}

.tab-item {
  font-size: 1rem;
  color: #666;
  padding: 0.6rem 1.5rem;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  user-select: none;
  min-width: 80px;
  text-align: center;
}

.tab-item.active {
  background: #fff;
  color: #42b883;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

/* 任务列表 */
.task-list-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section {
  margin-bottom: 0.5rem;
}

.task-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: #fff;
  border: 1px solid #e8e8e8;
  transition: all 0.25s ease;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.task-card:hover {
  border-color: #42b883;
  box-shadow: 0 4px 16px rgba(66, 184, 131, 0.15);
  transform: translateY(-2px);
}

.task-card.completed {
  background: #f8f8f8;
  border-color: #e0e0e0;
}

.task-card.completed .task-title {
  text-decoration: line-through;
  color: #999;
}

.task-checkbox {
  margin-left: auto;
}

.checkbox-circle {
  width: 28px;
  height: 28px;
  border: 2px solid #d0d0d0;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.25s ease;
  background: #fff;
}

.checkbox-circle:hover {
  border-color: #42b883;
  box-shadow: 0 0 0 4px rgba(66, 184, 131, 0.1);
}

.checkbox-circle.checked {
  background: #42b883;
  border-color: #42b883;
}

.checkmark {
  color: #fff;
  font-size: 16px;
  font-weight: bold;
}

.task-content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 0.8rem;
}

.task-title {
  font-size: 1rem;
  color: #333;
  font-weight: 500;
  line-height: 1.4;
}

.task-subtitle {
  font-size: 0.85rem;
  color: #888;
  font-weight: 400;
}

/* 已完成任务区域 */
.completed-section {
  margin-top: 1.5rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.completed-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #ebebeb 100%);
  padding: 1rem 1.25rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
}

.completed-header:hover {
  background: linear-gradient(135deg, #ebebeb 0%, #e0e0e0 100%);
}

.chevron {
  font-size: 14px;
  color: #666;
  transition: transform 0.3s ease;
  display: inline-block;
}

.chevron.expanded {
  transform: rotate(90deg);
}

.completed-title {
  font-size: 0.95rem;
  color: #555;
  font-weight: 500;
}

.completed-list {
  padding: 0.75rem 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* 空状态 */
.empty-tasks {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 3rem;
  color: #bbb;
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin-bottom: 1.25rem;
  color: #ccc;
}

.empty-icon svg {
  width: 100%;
  height: 100%;
}

.empty-tasks p {
  margin: 0;
  font-size: 0.95rem;
  color: #aaa;
}

.empty-detail {
  background: #fff;
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.empty-detail p {
  color: #999;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  max-width: 480px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
}

.execute-modal-content {
  max-width: 400px;
}

.add-modal-content {
  max-width: 520px;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.75rem;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #333;
  font-weight: 600;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #999;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-btn:hover {
  background: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 1.75rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.25rem 1.75rem;
  border-top: 1px solid #eee;
}

.btn {
  padding: 0.75rem 2rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn.ghost {
  background: #f2f2f2;
  color: #666;
}

.btn.ghost:hover {
  background: #e0e0e0;
}

.btn.primary {
  background: #42b883;
  color: #fff;
}

.btn.primary:hover {
  background: #369970;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-loading {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: btn-spin 0.8s linear infinite;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

/* 表单样式 */
.form-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.form-row:last-child {
  margin-bottom: 0;
}

.label {
  font-size: 0.9rem;
  color: #333;
}

.time-input {
  padding: 0.75rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-size: 1rem;
  background: #f8f8f8;
  cursor: pointer;
  transition: all 0.2s;
}

.time-input:focus {
  outline: none;
  border-color: #42b883;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(66, 184, 131, 0.1);
}

.time-row {
  margin-bottom: 1rem;
}

.time-row:last-of-type {
  margin-bottom: 0;
}

.checkbox-row {
  gap: 0.75rem;
  cursor: pointer;
}

.confirm-text {
  font-size: 0.95rem;
  color: #333;
  line-height: 1.6;
}

.title-input-wrap {
  width: 100%;
  margin-bottom: 1rem;
}

.title-textarea {
  width: 100%;
  min-height: 100px;
  padding: 1rem 1.25rem;
  background: #f8f8f8;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  font-size: 1rem;
  color: #333;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;
}

.title-textarea:focus {
  outline: none;
  border-color: #42b883;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(66, 184, 131, 0.1);
}

.triple-row {
  align-items: flex-start;
  gap: 1rem;
}

.triple-row .field {
  flex: 1;
  min-width: 0;
}

.triple-row .field label {
  display: block;
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-select,
.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #333;
  background: #f8f8f8;
  box-sizing: border-box;
  transition: all 0.2s;
  cursor: pointer;
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: #42b883;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(66, 184, 131, 0.1);
}

/* 右键菜单 */
.context-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
}

.context-menu {
  position: fixed;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 6px 0;
  min-width: 120px;
  z-index: 9999;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
  transition: background 0.2s;
}

.context-menu-item:hover {
  background: #f5f5f5;
}

.context-menu-icon {
  font-size: 1rem;
}

/* ==================== 定时任务区块 ==================== */
.schedule-section {
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #eee;
}

.schedule-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.schedule-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
}

.schedule-add-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: #42b883;
  color: #fff;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.schedule-add-btn:hover {
  background: #369970;
}

.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.schedule-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
  background: #f8f9fb;
  border-radius: 8px;
  border: 1px solid #eef0f3;
  transition: background 0.2s, border-color 0.2s;
}

.schedule-item:hover {
  background: #f1f4f8;
  border-color: #dde2ea;
}

.schedule-item.is-stopped {
  opacity: 0.7;
}

.schedule-info {
  flex: 1;
  min-width: 0;
}

.schedule-item-title {
  font-size: 0.92rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.run-tag {
  display: inline-block;
  padding: 1px 6px;
  background: #fff7e6;
  color: #d48806;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
}

.schedule-meta {
  margin-top: 4px;
  font-size: 0.75rem;
  color: #888;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta-dot {
  color: #ccc;
}

.schedule-switch-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.schedule-status-text {
  font-size: 0.7rem;
  color: #999;
}

.schedule-status-text.on {
  color: #42b883;
}

.schedule-switch {
  position: relative;
  width: 38px;
  height: 20px;
  border-radius: 10px;
  background: #ccc;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;
}

.schedule-switch.on {
  background: #42b883;
}

.schedule-switch:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.2s, transform 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.schedule-switch.on .switch-thumb {
  left: 20px;
}

.schedule-switch.loading .switch-thumb {
  animation: btn-spin 0.7s linear infinite;
  border: 2px solid #e0e0e0;
  border-top-color: #42b883;
  background: transparent;
  box-shadow: none;
}

.schedule-empty {
  padding: 1.5rem 0.5rem;
  text-align: center;
  color: #999;
  font-size: 0.85rem;
}

.schedule-empty p {
  margin: 0 0 0.5rem 0;
}

.schedule-empty-btn {
  border: 1px dashed #cfd6df;
  background: #fff;
  border-radius: 6px;
  color: #42b883;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.schedule-empty-btn:hover {
  border-color: #42b883;
  background: #f0faf4;
}

/* ==================== 定时任务弹窗表单 ==================== */
.sch-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.1rem;
}

.sch-row:last-child {
  margin-bottom: 0;
}

.sch-row-inline {
  flex-direction: row;
  gap: 1rem;
}

.sch-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.sch-label {
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
}

.sch-input {
  width: 100%;
  padding: 0.6rem 0.85rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.9rem;
  background: #fff;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.sch-input:focus {
  outline: none;
  border-color: #42b883;
}

.sch-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.sch-input-num {
  width: 120px;
}

.sch-input-time {
  width: 130px;
}

.chip-group {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.chip {
  padding: 0.4rem 0.95rem;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #666;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
}

.chip:hover:not(:disabled) {
  border-color: #42b883;
  color: #42b883;
}

.chip.active {
  background: #42b883;
  border-color: #42b883;
  color: #fff;
}

.chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loop-count-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.infinite-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #666;
  cursor: pointer;
}

.time-range-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.time-sep {
  color: #999;
}

/* 危险按钮 */
.btn.danger {
  background: #f44336;
  color: #fff;
}

.btn.danger:hover:not(:disabled) {
  background: #d32f2f;
}
</style>
